import { action, observable, computed, reaction } from "mobx";
import { addDays } from "date-fns";

import { api } from "../services";
import rollbar from "../services/rollbar";
import { productsStore } from ".";

class LeonardoInsurance {
  @observable owner = {
    civil_status: null,
    domicile: null,
    domicile_equals_residence: null,
    education: null,
    profession: null,
    year_license_driver: null,
    company: null,
    years_insured_in_the_last_five_years: null,
  };
}

class LeonardoStore {
  /**
   * Creates an instance of LeonardoStore.
   *
   * @param {ProductsStore} productsStore - The products store, from which available products,
   *                                        vehicle information and subscription features are read.
   */
  constructor(productsStore) {
    this.productsStore = productsStore;

    if (!productsStore) {
      console.warn("[LeonardoStore] productsStore is undefined!");
    }

    /**
     * Reaction to update the names of available products whenever
     * the list of available products or active subscriptions changes.
     */
    reaction(
      () => ({
        availableProducts: Array.isArray(this.productsStore?.availableProducts)
          ? this.productsStore.availableProducts.slice()
          : [],
        activeSubscriptionNames: this.activeSubscriptionNames.slice(),
      }),
      () => {
        this.populateSellableProductNames();
      },
      { fireImmediately: true }
    );

    /**
     * Reaction to handle changes to the selected plate or manageable vehicles list.
     */
    reaction(
      () => ({
        vehicles: this.productsStore.mypageStore.manageableVehicles.slice(),
        plate: this.selectedPlateNumber,
      }),
      ({ vehicles }) => {
        this.updateSubscriptionsFromSelectedVehicle(vehicles);
      }
    );
  }

  /**
   * Filters the product names in `product_names`, keeping only those present in `availableSellableProductNames`.
   * Directly updates `product_names` by removing unsellable products.
   *
   * @returns {void}
   */
  filterSellableProductNames() {
    this.product_names = this.product_names
      .map((productName) =>
        this.availableSellableProductNames.includes(productName)
          ? productName
          : null
      )
      .filter((name) => name !== null);
  }

  /**
   * Checks if a subscription is expiring within 30 days from now.
   *
   * @param {Object} subscription - The subscription object to check.
   * @param {Object} subscription.node - The node containing the end date.
   * @param {string} subscription.node.end_date - ISO string of subscription end date.
   * @returns {boolean} `true` if the subscription expires within 30 days, otherwise `false`.
   */
  isSubscriptionExpiring(subscription) {
    return (
      subscription &&
      subscription.node &&
      new Date(subscription.node.end_date) < addDays(new Date(), 30)
    );
  }

  /**
   * Checks if a subscription is active (i.e., not expiring).
   *
   * @param {Object} subscription - The subscription object to check.
   * @returns {boolean} `true` if the subscription is active, `false` if null or expiring.
   */
  isSubscriptionActive = (subscription) =>
    subscription && !this.isSubscriptionExpiring(subscription);

  // User
  @observable user = {};

  // Insurance variables
  @observable isSellingInsurance = false;
  @observable leonardoInsurance = new LeonardoInsurance();

  // ChatAgent variables
  @observable selectedPlateNumber = null;
  @observable selectedContractName = null;
  @observable selectedBuyInterest = null;
  @observable selectedBuyConfirmation = false;

  // Cart
  @observable cart = [];

  // Cross Selling variables
  @observable activeSubscriptionNames = [];
  @observable availableSellableProductNames = ["Assistenza BASE", "MyApp Air"];
  @observable userAcceptedProductNames = [];
  @observable userAcceptedChargeModels = [];
  @observable userRejectedProductNames = [];
  @observable currentOfferedProductName;
  @observable previousOfferedProductName = null;
  @observable lastCrossSellingConfirmed = false;
  @observable crossSellingCompleted = false;

  // PrivacyForm variables
  @observable checkedAgreedGeneralConditions = false;
  @observable checkedAgreedRestrictiveClauses = false;

  @observable airClubGeneral = null;
  @observable air_club_profiling = null;
  @observable air_club_marketing = null;
  @observable air_club_third_parties = null;

  @observable userHasSeenPrivacyForm = false;

  // Enabling modal submission
  @observable privacyEnabled = true;
  @observable paymentEnabled = true;

  // Computed: move in
  @computed get remoteUserIsFromMoVeInProgram() {
    return this.user?.hasOwnProperty("movein") && this.user.movein === true;
  }

  // Computed: privacy form
  @computed get areAllConditionsAccepted() {
    return (
      this.checkedAgreedGeneralConditions &&
      this.checkedAgreedRestrictiveClauses
    );
  }

  @computed get allPrivacyConsentsGiven() {
    return this.areAllConditionsAccepted && this.userHasSeenPrivacyForm;
  }

  // Cart status
  /**
   * Checks if the selected product has the "movein" feature.
   * Useful to determine if the user selected an activation service.
   */
  @computed get isProductMovein() {
    return (
      this.selectedContractName &&
      this.cart?.some(
        (product) =>
          product.name === this.selectedContractName &&
          product.features?.some((feature) => feature.node?.name === "movein")
      )
    );
  }

  /**
   * Checks if the selected product has the "myapp" feature.
   * Useful to verify if the MyApp integration is selected.
   */
  @computed get isMyAppAddon() {
    return (
      this.selectedContractName &&
      this.cart?.some(
        (product) =>
          product.name === this.selectedContractName &&
          product.features?.some((feature) => feature.node?.name === "myapp")
      )
    );
  }

  /**
   * Checks if the selected product is a renewal with the "air" feature.
   * Used to identify renewals related to "air" services.
   */
  @computed get isProductRenew() {
    return (
      this.selectedContractName &&
      this.cart?.some(
        (product) =>
          product.name === this.selectedContractName &&
          product.features?.some((feature) => feature.node?.name === "air") &&
          product.category === "renew"
      )
    );
  }

  /**
   * Checks if the selected product is a renewal with the "movein" feature.
   * Used to distinguish movein renewals from other types.
   */
  @computed get isProductMoveinRenewal() {
    return (
      this.selectedContractName &&
      this.cart?.some(
        (product) =>
          product.name === this.selectedContractName &&
          product.features?.some(
            (feature) => feature.node?.name === "movein"
          ) &&
          product.category === "renew"
      )
    );
  }

  /**
   * Checks if the selected product is an "add-on" with "axa-assistance"
   * feature and **without** the "reminder" feature.
   * Useful to identify explicit activation of assistance without reminder.
   */
  @computed get isAssistance() {
    return (
      this.selectedContractName &&
      this.cart?.some(
        (product) =>
          product.name === this.selectedContractName &&
          product.category === "add-on" &&
          product.features.some(
            (feature) => feature.node.name === "axa-assistance"
          ) &&
          !product.features.some((feature) => feature.node.name === "reminder")
      )
    );
  }

  /**
   * Subscribes to the products in the cart.
   * Accepts a payment method and optional refId.
   * Gathers cart products and prepares them for subscription.
   * If refId is provided, sets it as the default payment method for the user's Zuora account.
   * On error, logs to Rollbar and empties the cart.
   * @param {string} paymentMethod - Payment method for the subscription.
   * @param {string} [refId] - Optional payment method reference ID.
   * @returns {Promise<Object>} Subscription response with details.
   * @throws {Error} If subscription fails.
   */
  @action
  async subscribeToProduct(paymentMethod, refId) {
    let products = [];

    for (const item of this.cart) {
      products.push({
        productUuid: item.uuid, 
        productRatePlanId: item.productRatePlans[0].id,
      });
    }

    try {
      if (refId) {
        await api("setPaymentMethodDefault", this.user.zuora_account_id, refId);
      }

      const response = await api("subscribeToProduct", {
        zuora_account_id: this.user.zuora_account_id,
        products: products,
        uuid: this.user.uuid,
        licensePlate: this.selectedPlateNumber,
        paymentMethod: paymentMethod,
      });

      if (
        !response.subscription ||
        response.subscription.some((sub) => !sub.success)
      ) {
        console.error("Error on subscription", response);
        throw "Error on subscription";
      }

      return response;
    } catch (error) {
      rollbar.error(
        `Error while subscribing user with uuid ${
          this.user.uuid
        } to products ${JSON.stringify(
          products
        )} with payment method ${paymentMethod} ${
          refId ? `refId ${refId}` : ""
        } error: ${error}`
      );

      throw error;
    } finally {
      this.cart = [];
    }
  }

  /**
   * Populates `product_names` with available product names from `productsStore`
   * that are sellable and **not** currently active for the selected vehicle.
   * Also updates `currentOfferedProductName		` with the first available name, if any.
   *
   * @returns {void}
   */
  @action
  populateSellableProductNames() {

    const availableProducts = this.productsStore.availableProducts || [];

    const filteredProducts = availableProducts.filter((product) => {
      const isBuyable = this.productsStore.isBuyableProduct(product);
      const productName = this.productsStore.getProductNameByUuid(product.uuid);
      const isActive = this.activeSubscriptionNames.includes(productName);

      return isBuyable && !isActive;
    });

    this.product_names = filteredProducts.map((product) => product.name);

    this.filterSellableProductNames();

    this.currentOfferedProductName = this.product_names[0] || null;
  }

  /**
   * Updates `activeSubscriptionNames` based on the selected vehicle.
   * Looks for the selected plate inside `manageableVehicles`, extracts active subscriptions
   * and updates the state.
   *
   * @param {Array<Object>} manageableVehicles - List of vehicles manageable by the user.
   * @returns {void}
   */
  @action
  updateSubscriptionsFromSelectedVehicle(manageableVehicles) {
    if (manageableVehicles.length > 0 && this.selectedPlateNumber) {

      const selectedVehicle = manageableVehicles.find(
        (vehicle) => vehicle.license_plate === this.selectedPlateNumber
      );

      const subscriptions = selectedVehicle?.subscriptions || [];

      const activeSubscriptions = subscriptions.filter((sub) =>
        this.isSubscriptionActive(sub)
      );

      this.activeSubscriptionNames = activeSubscriptions.map((sub) =>
        this.productsStore.getProductNameByUuid(sub.product.uuid)
      );
    }
  }
}

export default new LeonardoStore(productsStore);
